import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  LinearProgress,
  Pagination,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  alpha,
  Chip,
  GlobalStyles
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import dayjs from 'dayjs';
import { IListagemPedidoDireto, PedidoDiretoService } from '../../../shared/services/api/vendas/pedidodireto/PedidoDiretoService';
import { FerramentasDaListagemVendas } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemVendas';
import {
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';

export const ListagemDePedidoDireto: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemPedidoDireto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const [autoComplete, setAutoComplete] = useState(true);
  const [data, setData] = useState(true);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'data';
  }, [searchParams]);

  const idcli = useMemo(() => {
    return searchParams.get('cliente') || '';
  }, [searchParams]);

  const inicial = useMemo(() => {
    return searchParams.get('inicial') || dayjs().startOf('month').format('DD/MM/YYYY');
  }, [searchParams]);

  const final = useMemo(() => {
    return searchParams.get('final') || dayjs().format('DD/MM/YYYY').toString();
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  function verificaFormato(data: string) {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    return regex.test(data);
  }

  useEffect(() => {
    if (tipo === 'data') {
      setData(true);
      setAutoComplete(false);
      setRows([]);
      setSearchParams({ tipo: 'data', pagina: '1' }, { replace: true });
      setTotalCount(0);
    } else {
      setAutoComplete(true);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'cliente', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
  }, [tipo]);

  useEffect(() => {
    if (tipo === 'data' && verificaFormato(inicial) && verificaFormato(final)) {
      setIsLoading(true);
      debounce(() => {
        PedidoDiretoService.getAll(pagina, tipo, inicial, final, '')
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else if (tipo === 'data') {
      setRows([]);
    }
  }, [inicial, final, pagina, tipo]);

  useEffect(() => {
    if (tipo === 'cliente' && idcli !== '') { 
      setIsLoading(true);
      debounce(() => {
        PedidoDiretoService.getAll(pagina, tipo, inicial, final, idcli)
          .then((result) => {
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else if (tipo === 'cliente') {
      setRows([]);
    }    
  }, [idcli, pagina, tipo]);
  const formatarDocumento = (documento: string) => {
    if (!documento) return '-';
    const apenasNumeros = documento.replace(/\D/g, '');
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return documento;
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '*::-webkit-scrollbar': {
            width: '8px',
            height: '8px',
          },
          '*::-webkit-scrollbar-track': {
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.5)
              : '#f1f1f1',
            borderRadius: '4px',
          },
          '*::-webkit-scrollbar-thumb': {
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.grey[600], 0.6)
              : alpha(theme.palette.grey[400], 0.6),
            borderRadius: '4px',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.grey[500], 0.8)
                : alpha(theme.palette.grey[500], 0.8),
            }
          }
        }}
      />

      <LayoutBaseDePagina
        titulo='Listagem de Pedido Direto'
        barraDeFerramentas={
          <FerramentasDaListagemVendas
            mostrarInputBasicSelect
            dataInicial={inicial}
            dataFinal={final}
            mostrarInputData={data}
            mostrarAutoComplete={autoComplete}
            textoDoSelect={tipo}
            textoBotaoNovo='Adicionar'
            aoClicarEmNovo={() => navigate('/pedidodireto/novo')}
            aoMudarDataInicial={inicial => setSearchParams({ inicial: inicial, final: final, tipo: tipo, pagina: '1' }, { replace: true })}
            aoMudarTextoDoSelect={textoselect => setSearchParams({ inicial: inicial, final: final, tipo: textoselect, pagina: '1' }, { replace: true })}
            aoMudarDataFinal={final => setSearchParams({ inicial: inicial, final: final, tipo: tipo, pagina: '1' }, { replace: true })}
            aoMudarTextoDoCliente={(texto) => setSearchParams({ cliente: texto, tipo: tipo, pagina: '1', inicial: inicial, final: final }, { replace: true })}
            dadosSelecao={[
              { nameBD: 'data', descricao: 'Data' },
              { nameBD: 'cliente', descricao: 'Cliente' },
            ]}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column', 
          mx: { xs: 1, sm: 2, md: 2, lg: 2 },
          my: 1
        }}>
          <Paper
            elevation={0}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.95)
                : theme.palette.background.paper,
              boxShadow: theme.shadows[1],
            }}
          >
            <TableContainer 
              sx={{ 
                flex: 1,
                overflow: 'auto'
              }}
            >
              <Table 
                sx={{ 
                  minWidth: '700px',
                  '& .MuiTableCell-root': {
                    whiteSpace: 'nowrap'
                  }
                }}
                stickyHeader // Cabeçalho fixo
              >
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.dark, 0.35)
                      : alpha(theme.palette.primary.main, 0.08),
                    '& th': {
                      fontWeight: 'bold',
                      color: theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.dark,
                      fontSize: '0.9rem', // Fonte aumentada
                      py: 1.2,
                      px: 1.5,
                      borderBottom: `2px solid ${theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.light, 0.2)
                        : alpha(theme.palette.primary.main, 0.2)}`,
                      position: 'sticky',
                      top: 0,
                      zIndex: 10
                    }
                  }}>
                    <TableCell sx={{ width: '90px' }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <CalendarIcon sx={{ fontSize: '16px' }} />
                        <Typography variant="body2" fontWeight="bold" fontSize="0.9rem">
                          Data
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '90px' }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <ReceiptIcon sx={{ fontSize: '16px' }} />
                        <Typography variant="body2" fontWeight="bold" fontSize="0.9rem">
                          Número
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <PersonIcon sx={{ fontSize: '16px' }} />
                        <Typography variant="body2" fontWeight="bold" fontSize="0.9rem">
                          Cliente
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '130px' }}>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <AssignmentIcon sx={{ fontSize: '16px' }} />
                        <Typography variant="body2" fontWeight="bold" fontSize="0.9rem">
                          CPF/CNPJ
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align='right' sx={{ width: '110px' }}>
                      <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
                        <MonetizationOnIcon sx={{ fontSize: '16px' }} />
                        <Typography variant="body2" fontWeight="bold" fontSize="0.9rem">
                          Valor
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow
                      key={row.idpedido}
                      hover
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease-in-out',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? (index % 2 === 0
                            ? alpha(theme.palette.background.default, 0.4)
                            : alpha(theme.palette.background.default, 0.6))
                          : (index % 2 === 0
                            ? 'transparent'
                            : alpha(theme.palette.grey[50], 0.4)),
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.15)
                            : alpha(theme.palette.primary.light, 0.05),
                          '& td': {
                            borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          },
                          '& .MuiSvgIcon-root': {
                            color: theme.palette.primary.main,
                          },
                          '& .MuiTypography-root': {
                            color: theme.palette.mode === 'dark'
                              ? theme.palette.text.primary
                              : theme.palette.text.primary,
                          },
                          '& .MuiChip-root': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.main, 0.25)
                              : alpha(theme.palette.primary.light, 0.1),
                            borderColor: theme.palette.primary.main,
                          }
                        },
                        '&:last-child td': {
                          borderBottom: 0,
                        },
                        '& td': {
                          py: 1,
                          px: 1.5,
                          fontSize: '0.9rem', // Fonte aumentada
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                          transition: 'border-color 0.15s ease-in-out',
                        }
                      }}
                      onClick={() => navigate(`/pedidodireto/${row.idpedido}`)}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarIcon 
                            color="action" 
                            sx={{ 
                              fontSize: '14px',
                              transition: 'color 0.15s ease-in-out',
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            fontWeight="medium" 
                            color="text.primary" 
                            fontSize="0.9rem"
                            sx={{
                              transition: 'color 0.15s ease-in-out',
                            }}
                          >
                            {dayjs(row.emissao).format('DD/MM/YYYY')}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={row.numero}
                          size="small"
                          color="primary"
                          variant={theme.palette.mode === 'dark' ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: 'bold',
                            minWidth: '45px',
                            height: '20px',
                            fontSize: '0.8rem', // Fonte aumentada
                            '& .MuiChip-label': {
                              px: 0.8
                            },
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.dark, 0.25)
                              : 'transparent',
                            borderColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.light, 0.3)
                              : alpha(theme.palette.primary.main, 0.3),
                            transition: 'all 0.15s ease-in-out',
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <PersonIcon 
                            color="action" 
                            sx={{ 
                              fontSize: '14px',
                              transition: 'color 0.15s ease-in-out',
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            fontWeight="medium" 
                            color="text.primary" 
                            fontSize="0.9rem"
                            sx={{
                              transition: 'color 0.15s ease-in-out',
                            }}
                          >
                            {row.nome || 'Não informado'}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AssignmentIcon 
                            color="action" 
                            sx={{ 
                              fontSize: '14px',
                              transition: 'color 0.15s ease-in-out',
                            }} 
                          />
                          <Typography 
                            variant="body2" 
                            fontFamily="monospace" 
                            color="text.secondary" 
                            fontSize="0.9rem"
                            sx={{
                              transition: 'color 0.15s ease-in-out',
                            }}
                          >
                            {formatarDocumento(row.cpf || '')}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align='right'>
                        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                         
                          <Box textAlign="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={row.valor >= 0 ? 'success.main' : 'error.main'}
                              sx={{
                                fontSize: '0.9rem', // Fonte aumentada
                                letterSpacing: '0.2px',
                                transition: 'color 0.15s ease-in-out',
                              }}
                            >
                              {row.valor.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL'
                              })}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              fontSize="0.7rem"
                              sx={{
                                transition: 'color 0.15s ease-in-out',
                              }}
                            >
                              Total
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>

                {/* Mensagem de lista vazia */}
                {totalCount === 0 && !isLoading && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={5} align='center' sx={{ py: 8 }}>
                        <Box>
                          <ReceiptIcon sx={{
                            fontSize: 56,
                            color: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.text.secondary, 0.4)
                              : alpha(theme.palette.text.secondary, 0.6),
                            mb: 2.5
                          }} />
                          <Typography variant='h5' color='text.secondary' gutterBottom fontSize="1.2rem" fontWeight="medium">
                            {Environment.LISTAGEM_VAZIA}
                          </Typography>
                          <Typography variant='body1' color='text.secondary' fontSize="0.95rem">
                            {idcli || (inicial && final) ? 'Tente ajustar os filtros de busca' : 'Nenhum pedido direto encontrado no período'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>

            {/* Footer separado para paginação */}
            {(totalCount > 0 && totalCount > Environment.LIMITE_DE_LINHAS) && (
              <Box sx={{
                py: 2,
                px: 1.5,
                borderTop: `2px solid ${theme.palette.mode === 'dark'
                  ? alpha(theme.palette.divider, 0.2)
                  : alpha(theme.palette.divider, 0.15)}`,
                backgroundColor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.default, 0.7)
                  : alpha(theme.palette.grey[50], 0.3),
                flexShrink: 0
              }}>
                <Box display='flex' justifyContent='center' alignItems='center'>
                  <Pagination
                    page={pagina}
                    count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHAS)}
                    onChange={(_, newPage) => setSearchParams({
                      cliente: idcli,
                      inicial: inicial,
                      final: final,
                      tipo: tipo,
                      pagina: newPage.toString()
                    }, { replace: true })}
                    color='primary'
                    size="medium"
                    shape='rounded'
                    showFirstButton
                    showLastButton
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                        fontSize: '0.85rem',
                        minWidth: '32px',
                        height: '32px',
                        backgroundColor: theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.paper, 0.8)
                          : undefined,
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.3)
                            : undefined
                        },
                        '&.Mui-selected': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.5)
                            : undefined,
                          color: theme.palette.mode === 'dark'
                            ? theme.palette.common.white
                            : undefined,
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark'
                              ? alpha(theme.palette.primary.main, 0.6)
                              : undefined
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Loading indicator no footer */}
            {isLoading && (
              <Box sx={{ 
                py: 1,
                px: 1.5,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                flexShrink: 0
              }}>
                <LinearProgress
                  sx={{
                    height: '2px',
                    backgroundColor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.dark, 0.3)
                      : alpha(theme.palette.primary.light, 0.2),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.primary.main,
                      borderRadius: '1px'
                    }
                  }}
                />
              </Box>
            )}
          </Paper>
        </Box>
      </LayoutBaseDePagina>
    </>
  );
};